import React, { useEffect } from 'react'
import {
  ColDef,
  GridOptions,
  IDetailCellRendererParams,
} from 'ag-grid-community'
import { useLazyGetGroupListQuery } from '../../../apis/firm-management/firm-management-api.api'
import type {
  FirmData,
  GroupListItem,
  GroupListRequest,
} from '../../../apis/firm-management/firm-management-api.def'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { useAppSelector } from '../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { StateCell } from './state-cell.tsx'
import { useTranslation } from 'react-i18next'
import { IdeyaButton } from '../../../common/antd/ideya-button.tsx'

type GroupQueryBase = Omit<GroupListRequest, 'firmId'>

interface GroupDetailRendererProps extends IDetailCellRendererParams<FirmData> {
  groupQueryBase?: GroupQueryBase
  onClick: () => void
}

const GroupDetailRenderer: React.FC<GroupDetailRendererProps> = ({
  data: firm,
  onClick,
  groupQueryBase = { status: 'all', page: 1 },
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const [trigger, { data, isFetching, isError, error }] =
    useLazyGetGroupListQuery()

  useEffect(() => {
    if (firm?.id) {
      trigger({ firmId: firm.id, ...groupQueryBase })
    }
  }, [firm?.id, groupQueryBase, trigger])

  const groups: GroupListItem[] = data?.data ?? []

  const errorMessage =
    error &&
    ('status' in error
      ? (() => {
          const payload = (error as FetchBaseQueryError).data as
            | { message?: string }
            | string
            | undefined
          return typeof payload === 'string'
            ? payload
            : payload?.message || 'Failed to load groups'
        })()
      : (error as SerializedError).message || 'Failed to load groups')

  if (isError) return <div style={{ color: 'red' }}>{errorMessage}</div>

  const CreateGroupButton = () => {
    return (
      <IdeyaButton ideyaType={'secondary'} onClick={onClick}>
        Create Group
      </IdeyaButton>
    )
  }

  const columns: ColDef[] = [
    {
      headerName: t('firm_management.group_table.columns.group_name'),
      field: 'name',
    },
    {
      headerName: t('firm_management.group_table.columns.region'),
      field: 'region',
    },
    {
      headerName: t('firm_management.group_table.columns.netCategory'),
      field: 'netCategory',
    },
    {
      headerName: t('firm_management.group_table.columns.state'),
      field: 'isActive',
      cellRenderer: StateCell,
      cellRendererParams: (params: { value: boolean }) => ({
        isActive: Boolean(params.value),
      }),
    },
    {
      headerName: t('firm_management.group_table.columns.action'),
      cellRenderer: CreateGroupButton,
      spanRows: true,
      autoHeight: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  ]

  // Important: no fixed height; let the inner grid grow with content
  const innerGridOptions: GridOptions<GroupListItem> = {
    domLayout: 'autoHeight',
    autoSizeStrategy: {
      type: 'fitGridWidth'
    }
  }

  return (
    <div
      style={{
        padding: `${themeStyle.paddingBase} ${themeStyle.paddingBase} ${themeStyle.paddingBase} 42px`,
      }}
    >
      <IdeyaAgGridTable
        loading={isFetching}
        columns={columns}
        rowData={groups}
        height={groups.length > 0 ? groups.length * 55 : 50}
        gridOptions={innerGridOptions}
        enableCellSpan={true}
      />
    </div>
  )
}

export default GroupDetailRenderer
